import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from "react"
import {ControlModel} from "../../../types/control"
import {useRPC} from "../../../hooks/rpc"
import {FanCoilControlComponent} from "./component"
import {useSelector} from "react-redux"
import {useDebounce} from "../../../hooks/debounce"
import {useQuestion} from "../../../hooks/question"
import {useLocalization} from "../../../hooks/localization"
import iconOff from "../../../assets/images/icons/off-icon.png"
import iconOn from "../../../assets/images/icons/on-icon.png"
import iconHeatActive from "../../../assets/images/icons/controls/mod-heat.png"
import iconHeatPassive from "../../../assets/images/icons/controls/mod-heat-passive.png"
import iconCoolingActive from "../../../assets/images/icons/controls/mod-cooling.png"
import iconCoolingPassive from "../../../assets/images/icons/controls/mod-cooling-passive.png"
import fan0 from "../../../assets/images/icons/fancoil-fan-a.png"
import fan1 from "../../../assets/images/icons/fancoil-fan-l.png"
import fan2 from "../../../assets/images/icons/fancoil-fan-m.png"
import fan3 from "../../../assets/images/icons/fancoil-fan-h.png"
import freshActive from "../../../assets/images/icons/fancoil-menu-active.png"
import freshPassive from "../../../assets/images/icons/fancoil-menu-passive.png"
type Props = {
    control: ControlModel
}

export const FanCoilControl = memo(function FanCoilControl({control}: Props) {
    const bridgeDeviceList = useSelector(state => state.control.bridgeDeviceList)
    const [rpc] = useRPC()
    const updateFanCoilTemperature = useDebounce(rpc.UpdateFanCoilTemperature, 250)
    const [whole, setWhole] = useState(control.temperature_settings?.whole || 14)
    const [fraction, setFraction] = useState(whole === 14 ? 5 : control.temperature_settings?.fraction || 0)
    const [fanSpeedStatus, setFanSpeedStatus] = useState(control.parameters["fan_status"] || "low")
    const [speed, setSpeed] = useState<number>(
        (fanSpeedStatus === "auto" && 0) ||
            (fanSpeedStatus === "low" && 1) ||
            (fanSpeedStatus === "mid" && 2) ||
            (fanSpeedStatus === "high" && 3) ||
            0
    )
    const [fanHeatingMode, setFanHeatingMode] = useState(control.parameters["control_mode_status"] || false)
    const [fancoilPowerStatus, setFancoilPowerStatus] = useState(control.parameters["control_power_status"] || false)
    const [ventilate, setVentilate] = useState(control.parameters["ventilate"] || false)
    const timeoutRef = useRef<number>()
    const controlRef = useRef<ControlModel>()
    const question = useQuestion()
    const {t} = useLocalization()
    controlRef.current = control

    const paramControl = useCallback(
        (mode: string) => {
            return control.parameters[mode]
        },
        [control.parameters]
    )

    useEffect(() => {
        timeoutRef.current = setTimeout(() => {
            if (whole !== controlRef.current.temperature_settings?.whole) {
                setWhole(controlRef.current.temperature_settings?.whole)
            }
            if (fraction !== controlRef.current.temperature_settings?.fraction) {
                setFraction(controlRef.current.temperature_settings?.fraction)
            }
            if (fancoilPowerStatus !== controlRef.current.parameters["control_power_status"]) {
                setFancoilPowerStatus(controlRef.current.parameters["control_power_status"])
            }
            if (fanHeatingMode !== controlRef.current.parameters["control_mode_status"]) {
                setFanHeatingMode(controlRef.current.parameters["control_mode_status"])
            }
            if (fanSpeedStatus !== controlRef.current.parameters["fan_status"]) {
                setFanSpeedStatus(controlRef.current.parameters["fan_status"])
            }
            if (ventilate !== controlRef.current.parameters["ventilate"]) {
                setVentilate(controlRef.current.parameters["ventilate"])
            }
            timeoutRef.current = null
        }, 2000)
        return () => clearTimeout(timeoutRef.current)
    }, [fraction, whole, fancoilPowerStatus, fanHeatingMode, fanSpeedStatus, ventilate])

    useEffect(() => {
        if (timeoutRef.current == null) {
            setWhole(control.temperature_settings?.whole)
            setFraction(control.temperature_settings?.fraction)
            setFancoilPowerStatus(control.parameters["control_power_status"])
            setFanHeatingMode(control.parameters["control_mode_status"])
            setFanSpeedStatus(control.parameters["fan_status"])
            setVentilate(control.parameters["ventilate"])
        }
    }, [control])

    const homeValueTemperature = useMemo(() => {
        for (const bridge_device of bridgeDeviceList) {
            if (bridge_device.id === control.temperature_settings?.bridge_device_id) {
                return [bridge_device.room_temperature_whole, bridge_device?.room_temperature_fraction]
            }
        }
        return [0, 0]
    }, [bridgeDeviceList, control])

    const temperatureClick = useCallback(
        (isPlus: boolean) => {
            let newWhole = whole
            let newFraction = fraction
            if (isPlus) {
                newFraction += 5
                if (newFraction >= 10) {
                    newFraction = 0
                    newWhole++
                }
            } else {
                newFraction -= 5
                if (newFraction < 0) {
                    newFraction = 5
                    newWhole--
                }
            }
            if (newWhole < 15) {
                newWhole = 14
                newFraction = 5
            } else if (newWhole > 32) {
                newWhole = 33
                newFraction = 0
            }
            setWhole(newWhole)
            setFraction(newFraction)
            updateFanCoilTemperature(
                control.id,
                newWhole,
                newFraction,
                fancoilPowerStatus,
                fanSpeedStatus,
                fanHeatingMode,
                ventilate
            )
        },
        [fraction, whole, control.id, updateFanCoilTemperature, fancoilPowerStatus, fanSpeedStatus, fanHeatingMode, ventilate]
    )

    const speedClick = useCallback(
        (isPlus: boolean) => {
            let newSpeed = speed
            let fanStatus = fanSpeedStatus
            if (isPlus) {
                if (newSpeed < 3) {
                    newSpeed++
                }
            } else {
                if (newSpeed > 0) {
                    newSpeed--
                }
            }
            setSpeed(newSpeed)
            if (newSpeed === 0) {
                fanStatus = "auto"
            } else if (newSpeed === 1) {
                fanStatus = "low"
            } else if (newSpeed === 2) {
                fanStatus = "mid"
            } else if (newSpeed === 3) {
                fanStatus = "high"
            }
            setFanSpeedStatus(fanStatus)
            updateFanCoilTemperature(
                control.id,
                whole,
                fraction,
                fancoilPowerStatus,
                fanStatus,
                fanHeatingMode,
                ventilate
            )
        },
        [
            speed,
            fanSpeedStatus,
            control.id,
            updateFanCoilTemperature,
            whole,
            fraction,
            fanHeatingMode,
            fancoilPowerStatus,
            ventilate
        ]
    )

    const clickFancoilPowerStatus = useCallback(() => {
        let newPowerStatus = fancoilPowerStatus
        if (newPowerStatus) {
            newPowerStatus = false
        } else {
            newPowerStatus = true
        }
        setFancoilPowerStatus(newPowerStatus)
        updateFanCoilTemperature(control.id, whole, fraction, newPowerStatus, fanSpeedStatus, fanHeatingMode, ventilate)
    }, [control.id, fancoilPowerStatus, updateFanCoilTemperature, fraction, whole, fanSpeedStatus, fanHeatingMode, ventilate])

    const clickHeating = useCallback(
        () =>
            question(t("pls_heating_mode"), () => {
                let heatingStatus = fanHeatingMode
                if (heatingStatus) {
                    heatingStatus = false
                } else {
                    heatingStatus = true
                }
                setFanHeatingMode(heatingStatus)
                updateFanCoilTemperature(control.id, whole, fraction, fancoilPowerStatus, fanSpeedStatus, heatingStatus, ventilate)
            }),
        [
            question,
            t,
            control.id,
            fanHeatingMode,
            updateFanCoilTemperature,
            whole,
            fraction,
            fanSpeedStatus,
            fancoilPowerStatus,
            ventilate
        ]
    )

    const clickVentilate = useCallback(()=> {
        let newVentilateStatus = ventilate
        if (newVentilateStatus) {
            newVentilateStatus = false
        } else {
            newVentilateStatus = true
        }
        setVentilate(newVentilateStatus)
        updateFanCoilTemperature(control.id, whole, fraction, fancoilPowerStatus, fanSpeedStatus, fanHeatingMode, newVentilateStatus)
    },[control.id, whole, fraction, fancoilPowerStatus, fanSpeedStatus, fanHeatingMode, ventilate, updateFanCoilTemperature])

    const powerIcon: string = useMemo(() => {
        if (fancoilPowerStatus) {
            return iconOn
        } else {
            return iconOff
        }
    }, [fancoilPowerStatus])

    const ventilateIcon: string = useMemo(() => {
        if (ventilate) {
            return freshActive
        } else {
            return freshPassive
        }
    }, [ventilate])

    const heatingIcon: string = useMemo(() => {
        if (fanHeatingMode) {
            if (paramControl("control_running_status")) {
                return iconHeatActive
            } else {
                return iconHeatPassive
            }
        } else {
            if (paramControl("control_running_status")) {
                return iconCoolingActive
            } else {
                return iconCoolingPassive
            }
        }
    }, [fanHeatingMode, paramControl])

    const iconSpeed: string = useMemo(() => {
        if (fanSpeedStatus === "auto") {
            return fan0
        } else if (fanSpeedStatus === "low") {
            return fan1
        } else if (fanSpeedStatus === "mid") {
            return fan2
        } else if (fanSpeedStatus === "high") {
            return fan3
        }
        return fan0
    }, [fanSpeedStatus])

    return (
        <FanCoilControlComponent
            clickHeatingMode={clickHeating}
            iconHeating={heatingIcon}
            clickOpenOff={clickFancoilPowerStatus}
            iconOpenOff={powerIcon}
            iconVentilate={ventilateIcon}
            clickVentilate={clickVentilate}
            control={control}
            temperatureClick={temperatureClick}
            speedClick={speedClick}
            homeValueTemperature={homeValueTemperature[0] + "." + homeValueTemperature[1]}
            valueTemperature={whole + "." + fraction}
            valueSpeed={iconSpeed}
            buttonStatus={fancoilPowerStatus}
        />
    )
})
